import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { 
  BarChart3, Users, LogOut, FileText, Brain, 
  ChevronRight, Menu, X, TrendingUp, AlertTriangle, BookOpen, 
  Rocket, MapPin, Bot, Clock, CheckSquare, Activity, Network,
  Zap, ClipboardCheck, Target, Lock, Briefcase, Factory,
  ShieldAlert, ClipboardList, Lightbulb
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import AdminUsers from "./AdminUsers";
import AdminBehavioral from "./AdminBehavioral";
import AdminAnalytics from "./AdminAnalytics";
import AdminReports from "./AdminReports";
import AdminDocuments from "./AdminDocuments";
import AdminIntervention from "./AdminInterventions";
import AdminFrictionMapping from "./AdminFrictionMapping";
import AdminCheckins from "./AdminCheckins";
import AdminWorkerProfiles from "./AdminWorkerProfiles";
import AdminPsychometrics from "./AdminPsychometrics";
import AdminNetworkMetrics from "./AdminNetworkMetrics";
import AdminFatigueManagement from "./AdminFatigueManagement";
import AdminExperimentResults from "./AdminExperimentResults";
import AdminPositions from "./AdminPositions";
import AdminDepartments from "./AdminDepartments";
import ActionQueue from "./ActionQueue";
import WorkerDiagnostics from "./WorkerDiagnostics";
import ClusterDiagnostics from "./ClusterDiagnostics";
import InterventionRecommendations from "./InterventionRecommendations";
import WorkerResults from "./WorkerResults";
import AdminInspectionData from "./AdminInspectionData";
import { IncidentSignals } from "@/components/admin/IncidentSignals";
import { IncidentManagement } from "@/components/admin/IncidentManagement";
import { RiskPulseSidebar } from "@/components/admin/RiskPulseSidebar";
import { SafetyCopilot } from "@/components/admin/SafetyCopilot";
import { UpgradeGate, getTierBadgeLabel } from "@/components/admin/UpgradeGate";
import { useSubscriptionFeatures, useUserCompanyId } from "@/hooks/useSubscriptionFeatures";
import { hasTierAccess } from "@/lib/subscriptionFeatures";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface SiteInfo {
  id: string;
  location_name: string;
  site_code: string | null;
  subscription_tier: string | null;
  max_workers: number | null;
  worker_count: number;
}

// Tab to tier mapping - null means free (no restriction)
type TierRequirement = 'professional' | 'enterprise' | 'enterprise_plus' | null;

const TAB_TIERS: Record<string, TierRequirement> = {
  // SIGNALS
  'reports': null,
  'incident-signals': null,
  'checkins': null,
  'inspection-data': 'professional',
  'psychometrics': null,
  'network-metrics': 'professional',
  // DIAGNOSE
  'incident-management': null,
  'diagnostics': 'professional',
  'cluster-diagnostics': 'professional',
  'friction': 'professional',
  'fatigue': 'enterprise',
  // ACT
  'recommendations': 'enterprise',
  'intervention': 'professional',
  'action-queue': null,
  // RESULTS
  'worker-results': 'enterprise',
  'experiments': 'enterprise',
  'analytics': null,
  'behavioral': null,
  // CONFIGURE
  'users': null,
  'positions': null,
  'departments': null,
  'documents': 'professional',
};

const SiteAdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast: toastHook } = useToast();

  // Get subscription data for reactive tier checks
  const { companyId: subCompanyId } = useUserCompanyId();
  const { subscription, hasFeature } = useSubscriptionFeatures(subCompanyId || undefined);
  const currentTier = subscription?.tier || 'free';

  const [activeTab, setActiveTab] = useState("analytics");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [currentSite, setCurrentSite] = useState<SiteInfo | null>(null);
  const [assignedSites, setAssignedSites] = useState<SiteInfo[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalReports: 0,
    pendingReports: 0
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      fetchStats(selectedSiteId);
    }
  }, [selectedSiteId]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/admin/login");
      return;
    }

    // Check for site_admin role
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "site_admin")
      .maybeSingle();

    if (!roleData) {
      // Not a site admin, check if they're a regular admin
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .in("role", ["admin", "super_admin"]);

      if (adminRole && adminRole.length > 0) {
        navigate("/admin");
        return;
      }

      toastHook({
        title: "Access Denied",
        description: "You don't have site admin privileges.",
        variant: "destructive"
      });
      navigate("/");
      return;
    }

    // Fetch assigned sites
    const { data: siteAssignments, error: assignError } = await supabase
      .from("site_admin_assignments")
      .select(`
        site_id,
        company_locations (
          id,
          location_name,
          site_code,
          subscription_tier,
          max_workers
        )
      `)
      .eq("user_id", user.id);

    if (assignError || !siteAssignments || siteAssignments.length === 0) {
      toastHook({
        title: "No Sites Assigned",
        description: "You are not assigned to any sites.",
        variant: "destructive"
      });
      navigate("/");
      return;
    }

    // Get worker counts for each site
    const sites: SiteInfo[] = [];
    for (const assignment of siteAssignments) {
      const loc = assignment.company_locations as any;
      if (loc) {
        const { count } = await supabase
          .from("worker_profiles")
          .select("id", { count: "exact", head: true })
          .eq("site_id", loc.id);

        sites.push({
          id: loc.id,
          location_name: loc.location_name,
          site_code: loc.site_code,
          subscription_tier: loc.subscription_tier,
          max_workers: loc.max_workers,
          worker_count: count || 0
        });
      }
    }

    setAssignedSites(sites);
    if (sites.length > 0) {
      setSelectedSiteId(sites[0].id);
      setCurrentSite(sites[0]);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toastHook({
      title: "Logged Out",
      description: "You have been successfully logged out."
    });
    navigate("/admin/login");
  };

  const fetchStats = async (siteId: string) => {
    try {
      const [usersRes, reportsRes, pendingRes] = await Promise.all([
        supabase.from("worker_profiles").select("id", { count: "exact", head: true }).eq("site_id", siteId),
        supabase.from("peer_reports").select("id", { count: "exact", head: true }).eq("site_id", siteId),
        supabase.from("peer_reports").select("id", { count: "exact", head: true }).eq("site_id", siteId).eq("verification_status", "pending_verification")
      ]);

      setStats({
        totalUsers: usersRes.count || 0,
        totalReports: reportsRes.count || 0,
        pendingReports: pendingRes.count || 0
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSiteChange = (siteId: string) => {
    setSelectedSiteId(siteId);
    const site = assignedSites.find(s => s.id === siteId);
    if (site) {
      setCurrentSite(site);
    }
  };

  // S-I-A-R Framework Navigation
  const navSections = [
    {
      label: "SIGNALS",
      items: [
        { id: "reports", label: "Reports", icon: FileText },
        { id: "incident-signals", label: "Incident Signals", icon: ShieldAlert },
        { id: "checkins", label: "Check-ins", icon: Clock },
        { id: "inspection-data", label: "Inspections", icon: ClipboardCheck },
        { id: "psychometrics", label: "Psychometrics", icon: ClipboardList },
        { id: "network-metrics", label: "Network Metrics", icon: Network },
      ]
    },
    {
      label: "DIAGNOSE",
      items: [
        { id: "incident-management", label: "Incident Management", icon: ShieldAlert },
        { id: "diagnostics", label: "Worker Diagnostics", icon: Activity },
        { id: "cluster-diagnostics", label: "Cluster Diagnostics", icon: Network },
        { id: "friction", label: "Friction Map", icon: MapPin },
        { id: "fatigue", label: "Fatigue Management", icon: AlertTriangle },
      ]
    },
    {
      label: "ACT",
      items: [
        { id: "recommendations", label: "AI Recommendations", icon: Zap },
        { id: "intervention", label: "Interventions", icon: Rocket },
        { id: "action-queue", label: "Action Queue", icon: CheckSquare },
      ]
    },
    {
      label: "RESULTS",
      items: [
        { id: "worker-results", label: "Worker Results", icon: Target },
        { id: "experiments", label: "Experiments", icon: Lightbulb },
        { id: "analytics", label: "Analytics", icon: BarChart3 },
        { id: "behavioral", label: "Behavioral", icon: Brain },
      ]
    },
    {
      label: "CONFIGURE",
      items: [
        { id: "users", label: t("admin.users"), icon: Users },
        { id: "positions", label: "Positions", icon: Briefcase },
        { id: "departments", label: "Departments", icon: Factory },
        { id: "documents", label: "Documents", icon: BookOpen },
      ]
    }
  ];

  // Helper to wrap content with UpgradeGate if needed
  const wrapWithGate = (content: React.ReactNode, tier: TierRequirement, featureName: string) => {
    if (!tier) return content;
    return (
      <UpgradeGate requiredTier={tier} featureName={featureName}>
        {content}
      </UpgradeGate>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "analytics":
        return <AdminAnalytics siteId={selectedSiteId} />;
      case "friction":
        return wrapWithGate(<AdminFrictionMapping siteId={selectedSiteId} />, TAB_TIERS['friction'], 'Friction Mapping');
      case "reports":
        return <AdminReports siteId={selectedSiteId} />;
      case "incident-signals":
        return <IncidentSignals siteId={selectedSiteId} />;
      case "checkins":
        return <AdminCheckins siteId={selectedSiteId} />;
      case "psychometrics":
        return <AdminPsychometrics siteId={selectedSiteId} />;
      case "network-metrics":
        return wrapWithGate(<AdminNetworkMetrics siteId={selectedSiteId} />, TAB_TIERS['network-metrics'], 'Network Metrics');
      case "documents":
        return wrapWithGate(<AdminDocuments siteId={selectedSiteId} />, TAB_TIERS['documents'], 'Document Management');
      case "users":
        return <AdminUsers siteId={selectedSiteId} />;
      case "positions":
        return <AdminPositions readOnly />;
      case "departments":
        return <AdminDepartments readOnly />;
      case "behavioral":
        return <AdminBehavioral siteId={selectedSiteId} />;
      case "intervention":
        return wrapWithGate(<AdminIntervention siteId={selectedSiteId} />, TAB_TIERS['intervention'], 'Interventions');
      case "action-queue":
        return <ActionQueue siteId={selectedSiteId} />;
      case "incident-management":
        return <IncidentManagement siteId={selectedSiteId} />;
      case "diagnostics":
        return wrapWithGate(<WorkerDiagnostics siteId={selectedSiteId} />, TAB_TIERS['diagnostics'], 'Worker Diagnostics');
      case "cluster-diagnostics":
        return wrapWithGate(<ClusterDiagnostics siteId={selectedSiteId} />, TAB_TIERS['cluster-diagnostics'], 'Cluster Diagnostics');
      case "recommendations":
        return wrapWithGate(<InterventionRecommendations siteId={selectedSiteId} />, TAB_TIERS['recommendations'], 'AI Recommendations');
      case "worker-results":
        return wrapWithGate(<WorkerResults siteId={selectedSiteId} />, TAB_TIERS['worker-results'], 'Worker Results');
      case "experiments":
        return wrapWithGate(<AdminExperimentResults siteId={selectedSiteId} />, TAB_TIERS['experiments'], 'Experiments');
      case "fatigue":
        return wrapWithGate(<AdminFatigueManagement siteId={selectedSiteId} />, TAB_TIERS['fatigue'], 'Fatigue Management');
      case "inspection-data":
        return wrapWithGate(<AdminInspectionData siteId={selectedSiteId} />, TAB_TIERS['inspection-data'], 'Inspection Data');
      default:
        return <AdminAnalytics siteId={selectedSiteId} />;
    }
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    trend,
    variant = "default"
  }: {
    title: string;
    value: number;
    icon: any;
    trend?: string;
    variant?: "default" | "warning" | "success";
  }) => (
    <Card className={cn(
      "border-0 shadow-sm hover:shadow-md transition-shadow",
      variant === "warning" && "bg-warning/5 border-l-4 border-l-warning",
      variant === "success" && "bg-success/5 border-l-4 border-l-success"
    )}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
            {trend && (
              <p className="text-xs text-success flex items-center gap-1 mt-2">
                <TrendingUp className="h-3 w-3" />
                {trend}
              </p>
            )}
          </div>
          <div className={cn(
            "p-3 rounded-2xl",
            variant === "warning" ? "bg-warning/10" : variant === "success" ? "bg-success/10" : "bg-primary/10"
          )}>
            <Icon className={cn(
              "h-6 w-6",
              variant === "warning" ? "text-warning" : variant === "success" ? "text-success" : "text-primary"
            )} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Render nav item with lock indicator (reactive to subscription tier)
  const renderNavItem = (item: { id: string; label: string; icon: any }, isMobile = false) => {
    const requiredTier = TAB_TIERS[item.id];
    // Only show lock if there's a required tier AND current tier doesn't meet it
    const isLocked = requiredTier !== null && !hasTierAccess(currentTier, requiredTier);

    return (
      <button
        key={item.id}
        onClick={() => {
          setActiveTab(item.id);
          if (isMobile) setMobileSidebarOpen(false);
        }}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors text-left",
          activeTab === item.id
            ? "bg-primary text-primary-foreground"
            : "hover:bg-secondary text-muted-foreground hover:text-foreground"
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {(sidebarOpen || isMobile) && (
          <>
            <span className="text-sm font-medium flex-1">{item.label}</span>
            {isLocked && requiredTier && (
              <div className="flex items-center gap-1">
                <Lock className={cn(
                  "h-3 w-3",
                  activeTab === item.id ? "text-primary-foreground/70" : "text-muted-foreground"
                )} />
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-[9px] px-1 py-0 h-4",
                    activeTab === item.id 
                      ? "border-primary-foreground/30 text-primary-foreground/70" 
                      : "border-muted-foreground/30"
                  )}
                >
                  {getTierBadgeLabel(requiredTier)}
                </Badge>
              </div>
            )}
          </>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex flex-col bg-card border-r transition-all duration-300",
        sidebarOpen ? "w-64" : "w-20"
      )}>
        {/* Logo */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-success flex items-center justify-center shrink-0">
              <img src="/src/assets/logo-symbol-white.svg" alt="SORIN" className="h-5 w-5" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-lg">Site Admin</h1>
                <p className="text-xs text-muted-foreground truncate max-w-[140px]">
                  {currentSite?.location_name || "Loading..."}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Site Selector */}
        {sidebarOpen && assignedSites.length > 1 && (
          <div className="p-4 border-b">
            <select
              value={selectedSiteId || ""}
              onChange={(e) => handleSiteChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-secondary text-sm"
            >
              {assignedSites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.location_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Nav Items - S-I-A-R Sections */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {navSections.map(section => (
            <div key={section.label}>
              {sidebarOpen && (
                <p className="text-[10px] font-semibold text-muted-foreground mb-2 px-2 tracking-wider">
                  {section.label}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map(item => renderNavItem(item))}
              </div>
            </div>
          ))}
        </nav>

        {/* Risk Pulse Widget in Sidebar */}
        <div className="p-4 border-t">
          <RiskPulseSidebar siteId={selectedSiteId} collapsed={!sidebarOpen} />
        </div>

        {/* Toggle & Logout */}
        <div className="p-4 border-t space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <ChevronRight className={cn("h-5 w-5 transition-transform", !sidebarOpen && "rotate-180")} />
            {sidebarOpen && <span>Collapse</span>}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span>{t("admin.logout")}</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50 lg:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-card z-50 lg:hidden shadow-2xl"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h1 className="font-bold">Site Admin</h1>
                    <p className="text-xs text-muted-foreground truncate max-w-[140px]">
                      {currentSite?.location_name}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setMobileSidebarOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {assignedSites.length > 1 && (
                <div className="p-4 border-b">
                  <select
                    value={selectedSiteId || ""}
                    onChange={(e) => handleSiteChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary text-sm"
                  >
                    {assignedSites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.location_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <nav className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-180px)]">
                {navSections.map(section => (
                  <div key={section.label}>
                    <p className="text-[10px] font-semibold text-muted-foreground mb-2 px-2 tracking-wider">
                      {section.label}
                    </p>
                    <div className="space-y-1">
                      {section.items.map(item => renderNavItem(item, true))}
                    </div>
                  </div>
                ))}
              </nav>
              <div className="p-4 border-t">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleLogout}
                >
                  <LogOut className="h-5 w-5" />
                  <span>{t("admin.logout")}</span>
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-30 pt-safe bg-background/80 backdrop-blur-xl border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold">Site Dashboard</h1>
                  {currentSite && (
                    <Badge variant="secondary" className="text-xs">
                      {currentSite.location_name}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Manage your site's safety operations
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button variant="outline" onClick={handleLogout} className="hidden sm:flex">
                <LogOut className="h-4 w-4 mr-2" />
                {t("admin.logout")}
              </Button>
            </div>
          </div>
        </header>

        {/* Dashboard Content - Full Width */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                title="Site Workers"
                value={stats.totalUsers}
                icon={Users}
              />
              <StatCard
                title="Total Reports"
                value={stats.totalReports}
                icon={FileText}
              />
              <StatCard
                title="Pending Review"
                value={stats.pendingReports}
                icon={AlertTriangle}
                variant={stats.pendingReports > 0 ? "warning" : "default"}
              />
            </div>

            {/* Main Content */}
            {renderContent()}
          </div>
        </main>

        {/* AI Copilot FAB */}
        <Button
          onClick={() => setCopilotOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-40"
          size="icon"
        >
          <Bot className="h-6 w-6" />
        </Button>

        {/* AI Safety Copilot */}
        <SafetyCopilot 
          isOpen={copilotOpen} 
          onClose={() => setCopilotOpen(false)} 
          siteId={selectedSiteId}
        />
      </div>
    </div>
  );
};

export default SiteAdminDashboard;
